/**
 * MapController is the single, stable control surface for the dashboard map. It is exposed as
 * `window.tapaMap` (see Map.create) so the AI assistant can drive the map without knowing about the
 * scattered buttons, custom events and globals the map is actually built from.
 *
 * Every method delegates to the exact same code paths the on-screen controls use (layer
 * enable/disable, the traffic/heatmap toggle buttons, DirectionsControl), so the UI state stays in
 * sync no matter whether a human clicks or the assistant calls a method here.
 *
 * The assistant only ever produces plain JSON actions (see Assistant.executeMapActions); this class
 * is the trusted layer that validates and applies them. It is intentionally side-effect-only and
 * returns booleans so a future client-side tool loop can report success back to the model.
 */

// Maps the assistant-facing layer names to the internal Map.dataLayers keys.
const LAYER_KEYS = {
  incidents: "incidents",
  psr: "parkingSpots",
  fsr: "fsrLocations",
  tsr: "tsrLocations",
};

export class MapController {
  /**
   * @param {import("./Map").Map} map The Map instance that owns the layers and controls.
   */
  constructor(map) {
    this.map = map;
  }

  /**
   * Shows or hides one of the data layers.
   *
   * @param {"incidents"|"psr"|"fsr"|"tsr"} name
   * @param {boolean} on
   * @returns {boolean} true if the layer exists and was toggled.
   */
  setLayer(name, on) {
    const key = LAYER_KEYS[name];
    const layer = key && this.map.dataLayers ? this.map.dataLayers[key] : null;
    if (!layer || typeof layer.enable !== "function") {
      return false;
    }
    if (on) {
      layer.enable();
    } else {
      layer.disable();
    }
    return true;
  }

  /**
   * Toggles the incident heatmap. The heatmap is only meaningful with the incidents layer present,
   * so enabling it first enables incidents (mirrors the UI, where the heatmap button is disabled
   * until incidents are on).
   *
   * @param {boolean} on
   * @returns {boolean}
   */
  setHeatmap(on) {
    if (on) {
      this.setLayer("incidents", true);
    }
    return this._setButtonState("toggleHeatMapButton", on);
  }

  /**
   * Toggles the Google traffic layer.
   *
   * @param {boolean} on
   * @returns {boolean}
   */
  setTraffic(on) {
    return this._setButtonState("toggleTrafficLayerButton", on);
  }

  /**
   * Plans and displays a driving route. Returns a promise that resolves once the route is drawn
   * (true) or planning failed (false); false synchronously when the map has no routing control.
   *
   * @param {{origin: string, destination: string, waypoints?: string[]}} options
   * @returns {Promise<boolean>|boolean}
   */
  planRoute(options) {
    const control = this.map.directionsControl;
    if (!control || typeof control.planRoute !== "function") {
      return false;
    }
    // A new standard route replaces whatever is on the map: resilience overlays (secondary
    // renderers, hover polylines, the resilience colour of the primary renderer) and a risk
    // assessment overlay would otherwise survive and paint over the new route.
    this.clearSafetyRoutes();
    this.clearRouteAssessment();
    return control.planRoute(options || {});
  }

  /**
   * Clears the currently displayed route.
   *
   * @returns {boolean}
   */
  clearRoute() {
    const control = this.map.directionsControl;
    if (!control || typeof control.clearRoute !== "function") {
      return false;
    }
    return control.clearRoute();
  }

  /**
   * Plans safety-optimized routes (standard vs. safety route, scored against secure parking and
   * incidents). Reads the Safety-tab filters for its parameters.
   *
   * @param {{origin?: string, destination?: string}} options
   * @returns {Promise<boolean>|boolean}
   */
  planSafetyRoute(options) {
    const control = this.map.safetyRouteController;
    if (!control || typeof control.planSafetyRoutes !== "function") {
      return false;
    }
    return control.planSafetyRoutes(options || {});
  }

  /**
   * Clears the safety routes and restores the standard renderer.
   *
   * @returns {boolean}
   */
  clearSafetyRoutes() {
    const control = this.map.safetyRouteController;
    if (!control || typeof control.clear !== "function") {
      return false;
    }
    return control.clear();
  }

  /**
   * Runs the route risk assessment for the route currently shown on the map (Route Assessment tab). The
   * optional inputs are copied into the form before the request so the UI reflects what was assessed.
   *
   * @param {{highValue?: boolean, dataWindowMonths?: number|null, departureTime?: string|null, driverHours?: {accumulatedDailyH?: number|null, continuousSinceBreakH?: number|null}}} options
   * @returns {Promise<boolean>|boolean}
   */
  assessRoute(options = {}) {
    const control = this.map.riskAssessmentController;
    if (!control || typeof control.assess !== "function") {
      return false;
    }
    return control.assess(options || {});
  }

  /**
   * Removes the risk assessment overlay and panel.
   *
   * @returns {boolean}
   */
  clearRouteAssessment() {
    const control = this.map.riskAssessmentController;
    if (!control || typeof control.clear !== "function") {
      return false;
    }
    return control.clear();
  }

  /**
   * Enables/disables the "limit markers to a radius around the route" filter and optionally sets the
   * radius (in km). The radius radios carry their value in km; selecting one drives the existing
   * recalculation events.
   *
   * @param {number|null} km
   * @param {boolean} on
   * @returns {boolean}
   */
  setRadius(km, on = true) {
    const limit = document.querySelector("input[name=limitRadius]");
    if (!limit) {
      return false;
    }
    if (on && km != null) {
      const radio = document.querySelector(`input[name=radius][value="${km}"]`);
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event("change"));
      }
    }
    limit.checked = on;
    limit.dispatchEvent(new Event("change"));
    return true;
  }

  /**
   * Returns a snapshot of what is currently shown on the map. Not used by the action channel
   * (phase A is fire-and-forget) but ready for a client-side tool loop that lets the model read map
   * state.
   *
   * @returns {object}
   */
  getState() {
    const layerOn = (name) => {
      const key = LAYER_KEYS[name];
      const layer = key && this.map.dataLayers ? this.map.dataLayers[key] : null;
      return !!(layer && typeof layer.isEnabled === "function" && layer.isEnabled());
    };

    let route = null;
    try {
      const directions = window.directionsRenderer && window.directionsRenderer.getDirections();
      const leg = directions && directions.routes && directions.routes[0] && directions.routes[0].legs[0];
      if (leg) {
        route = {
          origin: leg.start_address,
          destination: leg.end_address,
          distance: leg.distance ? leg.distance.text : null,
        };
      }
    } catch (e) {
      route = null;
    }

    return {
      layers: {
        incidents: layerOn("incidents"),
        psr: layerOn("psr"),
        fsr: layerOn("fsr"),
        tsr: layerOn("tsr"),
      },
      heatmap: this._isButtonActive("toggleHeatMapButton"),
      traffic: this._isButtonActive("toggleTrafficLayerButton"),
      route,
      safety:
        this.map.safetyRouteController && typeof this.map.safetyRouteController.getState === "function"
          ? this.map.safetyRouteController.getState()
          : null,
      riskAssessment:
        this.map.riskAssessmentController && typeof this.map.riskAssessmentController.getState === "function"
          ? this.map.riskAssessmentController.getState()
          : null,
    };
  }

  /**
   * Returns the geometry of the currently displayed standard route for rendering a static map
   * preview: the encoded overview polyline plus the resolved start/end coordinates and addresses.
   * Reads the same directionsRenderer as getState(). Returns null when no route is present, so
   * callers (e.g. the assistant's static map preview) can no-op cleanly.
   *
   * @returns {{polyline: string, origin: {lat: number, lng: number}, destination: {lat: number, lng: number}, originName: string, destinationName: string}|null}
   */
  getRouteGeometry() {
    try {
      const directions = window.directionsRenderer && window.directionsRenderer.getDirections();
      const route = directions && directions.routes && directions.routes[0];
      const leg = route && route.legs && route.legs[0];
      if (!route || !leg || !route.overview_polyline) {
        return null;
      }
      return {
        polyline: route.overview_polyline, // encoded polyline string
        origin: { lat: leg.start_location.lat(), lng: leg.start_location.lng() },
        destination: { lat: leg.end_location.lat(), lng: leg.end_location.lng() },
        originName: leg.start_address,
        destinationName: leg.end_address,
      };
    } catch (e) {
      return null;
    }
  }

  /**
   * Drives a toggle button to the desired on/off state by clicking it only when it is not already
   * there. This reuses the button's own click handler (the single source of truth for traffic and
   * heatmap behaviour) instead of duplicating its logic.
   *
   * @param {string} id
   * @param {boolean} on
   * @returns {boolean}
   */
  _setButtonState(id, on) {
    const button = document.getElementById(id);
    if (!button) {
      return false;
    }
    if (button.classList.contains("active") !== !!on) {
      button.click();
    }
    return true;
  }

  /**
   * @param {string} id
   * @returns {boolean}
   */
  _isButtonActive(id) {
    const button = document.getElementById(id);
    return !!(button && button.classList.contains("active"));
  }
}
